import React, { useEffect, createContext, useState } from "react";
import {
  getLessonOutlines,
  getLessonData,
  getSupportingContent,
} from "../services/lessons";
import { getMedia, getClients, getClientByUser } from "../services/clients";
import { getUserTypes } from "services/users";
import { useAuthContext } from "./AuthContext";


export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { activeUser } = useAuthContext();
  const [lessonID, setLessonID] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [
    lessonsSupportingContentID,
    setLessonsSupportingContentID,
  ] = useState();
  const [
    affirmationsSupportingContentID,
    setAffirmationsSupportingContentID,
  ] = useState();
  const [lessonOutlines, setLessonOutlines] = useState(null);
  const [supportingContent, setSupportingContent] = useState(null);
  const [clients, setClients] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [selectedClient, setSelectedClient] = useState();
  const [workflow, setWorkflow] = useState(null);
  const [archive, setArchive] = useState(null);
  const [theme, setTheme] = useState("default");
  const [media, setMedia] = useState(null);
  const [emailCampaigns, setEmailCampaigns] = useState([]);

  useEffect(() => {
    setTheme(localStorage.getItem("theme") || "default");
  }, []);

  useEffect(() => {
    if (activeUser) {
      if (activeUser.user_type === "admin") {
        const fetchClients = async () => {
          try {
            const fetched = await getClients();
            // The API may return an array or `{ clients: [...] }`; normalise before use.
            const clients = Array.isArray(fetched) ? fetched : fetched?.clients ?? [];
            setClients(clients);

            const storedSelectedClient = localStorage.getItem("selectedClient");
            if (storedSelectedClient) {
              const parsedClient = JSON.parse(storedSelectedClient);
              if (clients && clients.length > 0) {
                clients.forEach(client => {
                  if (client.id === parsedClient.id) {
                    setSelectedClient(client);
                    localStorage.setItem("selectedClient", JSON.stringify(client));
                  }
                });
              }
            } else {
              setSelectedClient(clients[0]);
            }
          } catch (error) {
            console.error(error);
          }
        };
        fetchClients();
      } else if (activeUser.user_type.includes("client")) {
        const fetchClient = async () => {
          try {
            const client = await getClientByUser(activeUser.public_id);
            setSelectedClient(client);
          } catch (error) {
            console.error(error);
          }
        };
        fetchClient();
      }

      const fetchUserTypes = async () => {
        try {
          const userTypes = await getUserTypes();
          setUserTypes(userTypes);
        } catch (error) {
          console.log("Can't fetch user types: ", error);
        }

      };

      fetchUserTypes();

    }
  }, [activeUser]);

  useEffect(() => {
    if (selectedClient) {
      localStorage.setItem("selectedClient", JSON.stringify(selectedClient));
    }
  }, [selectedClient]);

  useEffect(() => {
    const loadLessonOutlineData = async () => {
      if (lessonsSupportingContentID) {
        let response = await getLessonOutlines(lessonsSupportingContentID);
        if (response.success) {
          setLessonOutlines(response.supporting_content_items);
        }

        response = await getSupportingContent(lessonsSupportingContentID);
        if (response.success) {
          setSupportingContent(response.supporting_content);
        }
      }
    };

    loadLessonOutlineData();
  }, [lessonsSupportingContentID]);

  useEffect(() => {
    const loadLessonData = async () => {
      if (lessonID) {
        const response = await getLessonData(lessonID);
        if (response.success) {
          setLesson(response.supporting_content_items);
        }
      }
    };

    loadLessonData();
  }, [lessonID]);

  const fetchMedia = async () => {
    try {
      setMedia(await getMedia(selectedClient.public_id ?? selectedClient.id));
      console.log(media);
    } catch (error) {
      console.error(error);
    }
  };

  const addClient = (client) => {
    setClients([...clients, client]);
  };

  const hasPlatformFeature = (featureName) => {
    if (!selectedClient || !selectedClient.features) return false;
    return selectedClient.features.find(f => f.name === featureName);
  }

  return (
    <AppContext.Provider
      value={{
        lesson,
        setLesson,
        lessonID,
        setLessonID,
        lessonOutlines,
        setLessonOutlines,
        lessonsSupportingContentID,
        setLessonsSupportingContentID,
        affirmationsSupportingContentID,
        setAffirmationsSupportingContentID,
        supportingContent,
        setSupportingContent,
        clients,
        setClients,
        addClient,
        selectedClient,
        setSelectedClient,
        workflow,
        setWorkflow,
        archive,
        setArchive,
        hasPlatformFeature,
        theme,
        setTheme,
        media,
        setMedia,
        fetchMedia,
        emailCampaigns,
        setEmailCampaigns,
        userTypes
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
